import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../lib/apiBaseUrl';
import { fetchUserAvatar } from '../lib/optimizedApi';

interface UseUserAvatarReturn {
  avatarUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook to ensure consistent avatar display across the app
 * Handles Google SSO avatars, uploaded avatars, and fallbacks
 */
export function useUserAvatar(
  userId?: number | string,
  userEmail?: string,
  providedAvatarUrl?: string | null
): UseUserAvatarReturn {
  const { user } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadAvatar = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Priority 1: Use provided avatar URL if available
        if (providedAvatarUrl) {
          setAvatarUrl(providedAvatarUrl);
          setIsLoading(false);
          return;
        }

        // Priority 2: For current user, use auth context avatar
        if (!userId || (user && String(userId) === String(user.id))) {
          if (user?.avatar_url) {
            setAvatarUrl(user.avatar_url);
          } else {
            // No avatar for current user
            setAvatarUrl(null);
          }
          setIsLoading(false);
          return;
        }

        // Priority 3: Fetch avatar for other users
        if (userId) {
          const fetchedAvatar = await fetchUserAvatar(Number(userId));
          setAvatarUrl(fetchedAvatar);
        } else {
          setAvatarUrl(null);
        }
      } catch (err) {
        console.error('Error loading avatar:', err);
        setError(err as Error);
        setAvatarUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadAvatar();
  }, [userId, userEmail, providedAvatarUrl, user]);

  return { avatarUrl, isLoading, error };
}

/**
 * Get a consistent avatar URL with proper fallback handling
 */
export function getConsistentAvatarUrl(
  avatarUrl: string | null | undefined,
  email: string | undefined,
  name: string | undefined
): string | null {
  // If we have an avatar URL, use it
  if (avatarUrl) {
    // Handle data URLs (base64)
    if (avatarUrl.startsWith('data:')) {
      return avatarUrl;
    }
    
    // Handle full URLs (http/https)
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    }
    
    // Handle relative paths
    return `${API_BASE_URL}${avatarUrl}`;
  }
  
  // No avatar URL - return null to trigger initials fallback
  return null;
}
